import { Injectable, Injector, inject, signal } from '@angular/core';
import {
  ChatAuthService,
  ChatIntegrationStatus,
  ChatIntegrationType,
  ChatUserDataService
} from '@mitel-internal/cloudlink-components-chat';

// Off: Cloudlink avatars only. Test: every user gets TEST_HOST_AVATAR, as a plugin host would push its own.
// Removed: every user gets a host null (avatar deleted on the host), which shows initials.
export type HostAvatarMode = 'off' | 'test' | 'removed';

const NEXT_HOST_AVATAR_MODE: Record<HostAvatarMode, HostAvatarMode> = { off: 'test', test: 'removed', removed: 'off' };

const TEST_HOST_AVATAR =
  'data:image/svg+xml,' +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64"><rect width="64" height="64" fill="#7c3aed"/>' +
      '<text x="32" y="39" font-family="sans-serif" font-size="18" font-weight="700" fill="#fff" text-anchor="middle">HOST</text></svg>'
  );

const MS_GRAPH_TOKEN_WORKFLOW_URL =
  'https://workflow.us.dev.api.mitel.io/2017-09-01/webhooks/accounts/66d43e89-b3d2-477e-b88e-a245c4657137' +
  '/activities/98518769-8fa5-43d6-b54b-9b344918d0ca/workers?sync=true';

const ACCESS_TOKEN_KEYS = ['accessToken', 'access_token', 'token'];

// The workflow's output shape isn't fixed - take a bare string, or the first token-like field at any depth
function findAccessToken(value: unknown, depth = 0): string | null {
  if (typeof value === 'string') {
    const token = value
      .trim()
      .replace(/^"|"$/g, '')
      .replace(/^Bearer\s+/i, '');
    return depth === 0 && token && !/\s/.test(token) ? token : null;
  }
  if (!value || typeof value !== 'object' || depth > 5) {
    return null;
  }
  const record = value as Record<string, unknown>;
  for (const key of ACCESS_TOKEN_KEYS) {
    if (typeof record[key] === 'string' && record[key]) {
      return (record[key] as string).replace(/^Bearer\s+/i, '');
    }
  }
  for (const child of Object.values(record)) {
    const token = findAccessToken(child, depth + 1);
    if (token) {
      return token;
    }
  }
  return null;
}

function describeFields(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.slice(0, 3).map(describeFields);
  }
  if (value && typeof value === 'object') {
    return Object.fromEntries(Object.entries(value).map(([key, child]) => [key, describeFields(child)]));
  }
  return typeof value;
}

// What Graph checks - audience, delegated scopes or app roles, expiry and whose token it is - but not the signature
function describeTokenClaims(token: string): unknown {
  try {
    const payload = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const { aud, scp, roles, exp, upn, unique_name, tid } = JSON.parse(atob(payload));
    return {
      aud,
      scp,
      roles,
      expires: exp ? new Date(exp * 1000).toISOString() : undefined,
      expired: exp ? exp * 1000 <= Date.now() : undefined,
      user: upn ?? unique_name,
      tid
    };
  } catch {
    return 'not a JWT';
  }
}

// Live, in-context dev toggles for the currently-open chat (Offline/Destroy/Preview/Toggle
// Suggestions/Host avatar). Shared between AppComponent (the header trigger) and ChatPageComponent (which
// actually applies these to <mi-chat>) - signals rather than plain fields so ChatPageComponent's
// OnPush view reliably re-renders when the header (a different component) flips one of these.
@Injectable({ providedIn: 'root' })
export class DevToolsService {
  // Library services are resolved on use, not injected - AppComponent constructs this service before
  // Config.global is set, and the library's services must not be created that early (see AppComponent)
  private readonly injector = inject(Injector);

  readonly hostOffline = signal(false);
  readonly destroyed = signal(false);
  readonly previewMode = signal(false);
  readonly showingSuggestions = signal(false);
  readonly hostAvatarMode = signal<HostAvatarMode>('off');

  toggleHostOffline(): void {
    this.hostOffline.update(value => !value);
  }

  toggleDestroyed(): void {
    this.destroyed.update(value => !value);
  }

  togglePreviewMode(): void {
    this.previewMode.update(value => !value);
  }

  toggleShowingSuggestions(): void {
    this.showingSuggestions.update(value => !value);
  }

  // Users the library has asked this demo host about - only Cloudlink users get asked, so contacts, SMS numbers
  // and the AI assistant never get a host avatar here, same as with a real host
  private readonly hostAvatarUserIds = new Set<string>();

  // Stands in for lib-cl-chat's bridge: a handler for users loaded from now on (and those queued while Off), plus a
  // push for those already answered
  cycleHostAvatarMode(): void {
    const mode = NEXT_HOST_AVATAR_MODE[this.hostAvatarMode()];
    this.hostAvatarMode.set(mode);

    const userDataService = this.injector.get(ChatUserDataService);
    if (mode === 'off') {
      userDataService.registerGetUsersDataHandler(null);
      this.hostAvatarUserIds.forEach(userId => userDataService.clearUserData(userId));
      // Clearing drops all of a user's host data, so put the logged-in user's integration back
      if (this.msGraphIntegrationPushed) {
        this.pushMsGraphIntegration();
      }
      return;
    }

    const avatar = mode === 'test' ? TEST_HOST_AVATAR : null;
    userDataService.registerGetUsersDataHandler(async userIds => {
      userIds.forEach(userId => this.hostAvatarUserIds.add(userId));
      return userIds.map(userId => ({ userId, data: { avatar } }));
    });
    this.hostAvatarUserIds.forEach(userId => userDataService.updateUserData(userId, { avatar }));
  }

  // Stands in for the shell's msgraph integration: a Microsoft Graph token (fetched from the dev workflow, or pasted from
  // Graph Explorer, with Files.ReadWrite) is pushed as the logged-in user's integration, so OneDrive can be used.
  // Kept in memory only - it's a real credential.
  readonly msGraphTokenSet = signal(false);
  private msGraphToken: string | null = null;
  private msGraphIntegrationPushed = false;

  // Uses a token from the dev workflow unless one is pasted. The fetched token never goes through the prompt: Chrome
  // cuts a long default value short, and Graph rejects the cut token with a 401.
  async promptMsGraphToken(): Promise<void> {
    const fetched = await this.fetchMsGraphToken();
    const input = window.prompt(
      fetched
        ? 'Fetched a Microsoft Graph access token from the dev workflow - press OK to use it.' +
            ' Or paste another with Files.ReadWrite, or type "off" to disconnect.'
        : "Couldn't fetch a token from the dev workflow (see console). Paste one with Files.ReadWrite" +
            ' (Graph Explorer > Access token). Leave empty to disconnect.',
      ''
    );
    if (input === null) {
      return;
    }
    const pasted = input.trim().replace(/^Bearer\s+/i, '');
    this.msGraphToken = pasted.toLowerCase() === 'off' ? null : pasted || fetched;
    this.msGraphTokenSet.set(!!this.msGraphToken);
    this.pushMsGraphIntegration();
  }

  // Dev-only workflow that hands the logged-in Cloudlink user a Microsoft Graph token
  private async fetchMsGraphToken(): Promise<string | null> {
    const clToken = this.injector.get(ChatAuthService).token?.access_token;
    if (!clToken) {
      console.warn('DevToolsService.fetchMsGraphToken(): not logged in to Cloudlink');
      return null;
    }
    try {
      const response = await fetch(MS_GRAPH_TOKEN_WORKFLOW_URL, {
        method: 'POST',
        headers: { Authorization: `Bearer ${clToken}`, 'Content-Type': 'application/json' },
        body: '{}'
      });
      if (!response.ok) {
        console.warn('DevToolsService.fetchMsGraphToken(): workflow returned', response.status);
        return null;
      }
      const text = await response.text();
      let body: unknown = text;
      try {
        body = JSON.parse(text);
      } catch {
        // A bare token
      }
      const token = findAccessToken(body);
      if (!token) {
        // Field names only - the values are credentials
        console.warn('DevToolsService.fetchMsGraphToken(): no token in the response, fields:', describeFields(body));
      } else {
        console.info('DevToolsService.fetchMsGraphToken(): token claims', describeTokenClaims(token));
      }
      return token;
    } catch (error) {
      console.warn('DevToolsService.fetchMsGraphToken(): request failed', error);
      return null;
    }
  }

  private pushMsGraphIntegration(): void {
    const userId = this.injector.get(ChatAuthService).userId;
    if (!userId) {
      return;
    }
    this.msGraphIntegrationPushed = true;
    this.injector.get(ChatUserDataService).updateUserData(userId, {
      integrations: [
        this.msGraphToken
          ? {
              type: ChatIntegrationType.MSGRAPH,
              status: ChatIntegrationStatus.CONNECTED,
              data: { accessToken: this.msGraphToken }
            }
          : { type: ChatIntegrationType.MSGRAPH, status: ChatIntegrationStatus.DISCONNECTED }
      ]
    });
  }
}
